/**
 * @file DocumentCompactList.tsx
 * @description Список документов во вкладке «Документы» сделки и заказа
 * (phase19.31, часть 3): те же кусочки строки, что в реестре — «вид номер …
 * сумма», второй линией «дата · контрагент», пилюля статуса справа.
 *
 * Строка целиком — ссылка на карточку документа. `highlightedId` — строка,
 * на которую привёл узел «Пути заказа» (`?focus=`): обводка на пару секунд,
 * `data-row-id` — для прокрутки к ней (`useFocusedRow`).
 */

import { Link } from "react-router";

import { DocumentStatusPill, DocumentTitle } from "./document-cells";
import { documentCounterparty, documentDate, formatDocMoney } from "./document-view";
import { LIST_FRAME_CLASS } from "../../ui/ListStates";
import type { DocumentDto } from "../../../lib/api-crm";
import { cn } from "@/lib/utils";

interface Props {
  items: DocumentDto[];
  /** База адресов магазина: `/shop/<slug>`. */
  base: string;
  /** Строка, которую надо подсветить; `null` — никакую. */
  highlightedId?: string | null;
  "aria-label": string;
}

export function DocumentCompactList({ items, base, highlightedId = null, "aria-label": ariaLabel }: Props) {
  return (
    <ul aria-label={ariaLabel} className={cn(LIST_FRAME_CLASS, "divide-y divide-[var(--border)]")}>
      {items.map((doc) => {
        const second = [documentDate(doc), documentCounterparty(doc)].filter(Boolean).join(" · ");
        return (
          <li key={doc.id} data-row-id={doc.id}>
            <Link
              to={`${base}/crm/documents/${doc.id}`}
              aria-label={`Документ ${doc.number || "черновик"}`}
              className={cn(
                "flex min-h-18 min-w-0 items-center gap-3 px-3 py-2 outline-none transition-colors hover:bg-[var(--row-hover)] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                // Обводка на пару секунд: без неё переход из «Пути» выглядит как «ничего не произошло».
                highlightedId === doc.id && "ring-2 ring-inset ring-primary",
              )}
            >
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex min-w-0 items-center justify-between gap-2">
                  <span className="type-body min-w-0 truncate text-on-surface">
                    <DocumentTitle doc={doc} />
                  </span>
                  <span className="type-body shrink-0 font-semibold tabular-nums whitespace-nowrap">
                    {formatDocMoney(doc.totalGross, doc.currency)}
                  </span>
                </span>
                <span className="flex min-w-0 flex-wrap items-center justify-between gap-x-2 gap-y-1">
                  <span className="type-caption min-w-0 truncate tabular-nums text-on-surface-variant">{second}</span>
                  <DocumentStatusPill status={doc.status} />
                </span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
